import ReportLead from './ReportLead';
import { Box, Card, CardContent, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getReportSurfaceSx } from './report-design';

const eyebrowSx = {
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.12em',
    lineHeight: 1.2,
    textTransform: 'uppercase',
};

const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const performanceMetricValue = (metric) => number(metric?.count ?? metric?.total ?? metric);

export const hasPerformanceMetricValue = (metric) => {
    if (metric === null || metric === undefined || metric === '') return false;
    if (typeof metric === 'object') {
        const raw = metric.count ?? metric.total;
        return raw !== null && raw !== undefined && raw !== '';
    }
    return true;
};

export const performanceMetricDelta = (metric) => {
    const raw = metric?.delta;
    if (raw === null || raw === undefined || raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
};

export const buildPaidMediaJourneyMetrics = (data = {}, formatters = {}) => {
    const impressions = performanceMetricValue(data.impressions);
    const clicks = performanceMetricValue(data.clicks);
    const conversions = performanceMetricValue(data.conversions);
    const revenueAvailable = hasPerformanceMetricValue(data.revenue);
    const revenue = revenueAvailable ? performanceMetricValue(data.revenue) : null;
    const ctr = performanceMetricValue(data.ctr) || (impressions ? (clicks / impressions) * 100 : 0);
    const conversionRate = performanceMetricValue(data.convrate) || (clicks ? (conversions / clicks) * 100 : 0);
    const formatNumber = formatters.number || ((value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value));
    const formatCurrency = formatters.currency || ((value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value));
    const formatPercent = formatters.percent || ((value) => `${number(value).toFixed(2)}%`);

    return [
        { key: 'impressions', stage: 'Reach', label: 'Impressions', value: impressions, formatted: formatNumber(impressions), detail: 'Ads delivered', delta: performanceMetricDelta(data.impressions) },
        { key: 'clicks', stage: 'Response', label: 'Clicks', value: clicks, formatted: formatNumber(clicks), detail: 'Visits generated', delta: performanceMetricDelta(data.clicks) },
        { key: 'ctr', stage: 'Response', label: 'Click-through rate', value: ctr, formatted: formatPercent(ctr), detail: 'Clicks per impression', delta: performanceMetricDelta(data.ctr) },
        { key: 'conversions', stage: 'Outcome', label: 'Conversions', value: conversions, formatted: formatNumber(conversions), detail: 'Attributed actions', delta: performanceMetricDelta(data.conversions) },
        { key: 'conversion-rate', stage: 'Outcome', label: 'Conversion rate', value: conversionRate, formatted: formatPercent(conversionRate), detail: 'Conversions per click', delta: performanceMetricDelta(data.convrate) },
        { key: 'revenue', stage: 'Outcome', label: 'Revenue', value: revenue, formatted: revenueAvailable ? formatCurrency(revenue) : 'Not available', detail: revenueAvailable ? 'Attributed value' : 'Provider did not return revenue', delta: performanceMetricDelta(data.revenue) },
    ];
};

const defaultStageAccents = (theme) => ({
    Reach: theme.palette.primary.main,
    Acquisition: theme.palette.primary.main,
    Response: theme.palette.secondary.main,
    Engagement: theme.palette.secondary.main,
    Outcome: theme.palette.primary.dark,
});

const PerformanceMetric = ({ metric, accent, showStage, loading, plain }) => {
    const theme = useTheme();
    const hasDelta = metric.delta !== null && metric.delta !== undefined && metric.delta !== '' && Number.isFinite(Number(metric.delta));
    const delta = Number(metric.delta);
    const deltaIsPositive = delta >= 0;

    return (
        <Box
            sx={{
                position: 'relative',
                zIndex: 1,
                minWidth: 0,
                p: { xs: 1.5, md: 1.75 },
                borderRadius: 2.5,
                bgcolor: alpha('#ffffff', 0.97),
                border: `1px solid ${alpha(accent, 0.18)}`,
                boxShadow: `0 12px 28px ${alpha('#081f3d', 0.08)}`,
                ...(plain ? { ...getReportSurfaceSx(theme), boxShadow: 'none' } : {}),
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    bgcolor: accent,
                },
            }}
        >
            {showStage ? <Typography sx={{ ...eyebrowSx, color: accent }}>{metric.stage}</Typography> : null}
            <Typography sx={{ ...eyebrowSx, mt: showStage ? 0.5 : 0, color: 'text.secondary' }}>{metric.label}</Typography>
            {loading ? <Skeleton width="72%" height={40} sx={{ mt: 0.25 }} /> : (
                <Typography
                    title={String(metric.formatted ?? metric.value ?? '—')}
                    sx={{
                        mt: 0.65,
                        fontSize: { xs: '1.55rem', md: '1.75rem', lg: '1.4rem', xl: '1.65rem' },
                        lineHeight: 1.05,
                        fontWeight: 850,
                        letterSpacing: '-0.04em',
                        color: 'text.primary',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {metric.formatted ?? metric.value ?? '—'}
                </Typography>
            )}
            <Box sx={{ mt: 0.8, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">{metric.detail}</Typography>
                {hasDelta && !loading ? (
                    <Chip
                        size="small"
                        label={`${deltaIsPositive ? '+' : ''}${delta.toFixed(1)}%`}
                        aria-label={`${metric.label} ${deltaIsPositive ? 'increased' : 'decreased'} ${Math.abs(delta).toFixed(1)} percent versus previous period`}
                        sx={{
                            height: 21,
                            fontSize: '0.68rem',
                            fontWeight: 800,
                            color: deltaIsPositive ? 'success.dark' : 'error.dark',
                            bgcolor: deltaIsPositive ? alpha('#2e7d32', 0.09) : alpha('#d32f2f', 0.09),
                        }}
                    />
                ) : null}
            </Box>
        </Box>
    );
};

export const groupPerformanceStages = (metrics = [], stageDescriptions = {}, stageAccents = {}) => {
    const groups = [];
    metrics.forEach((metric) => {
        const last = groups[groups.length - 1];
        if (last?.label === metric.stage) {
            last.count += 1;
            return;
        }
        groups.push({
            label: metric.stage,
            count: 1,
            description: stageDescriptions[metric.stage] || '',
            accent: stageAccents[metric.stage],
        });
    });
    return groups;
};

export const PerformanceHighlightsGrid = ({ items = [], ariaLabel = 'Performance highlights', loading = false, plain = false }) => {
    const theme = useTheme();
    const visibleItems = items.filter(Boolean).slice(0, 4);

    if (!visibleItems.length) return null;

    return (
        <Box
            component="section"
            aria-label={ariaLabel}
            sx={{
                display: 'grid',
                gap: 1.25,
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                },
            }}
        >
            {visibleItems.map((item, index) => {
                const accent = item.accent || theme.palette.primary.main;
                const lines = Array.isArray(item.lines) ? item.lines.filter(Boolean) : [];
                return (
                    <Card
                        key={item.key || item.eyebrow || item.title || index}
                        variant="outlined"
                        sx={{
                            minWidth: 0,
                            minHeight: '100%',
                            overflow: 'hidden',
                            borderRadius: { xs: 3, md: 4 },
                            borderColor: alpha(accent, 0.16),
                            background: item.tone === 'soft'
                                ? `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha('#ffffff', 0.92)} 100%)`
                                : alpha('#ffffff', 0.82),
                            backdropFilter: 'blur(18px)',
                            boxShadow: `0 18px 42px ${alpha(accent, 0.08)}`,
                            ...(plain ? { ...getReportSurfaceSx(theme), backdropFilter: 'none' } : {}),
                        }}
                    >
                        <CardContent sx={{ display: 'grid', alignContent: 'start', gap: 0.65, height: '100%' }}>
                            <Typography sx={{ ...eyebrowSx, color: item.eyebrowColor || 'text.secondary' }}>
                                {item.eyebrow}
                            </Typography>
                            {loading ? <Skeleton width="72%" height={30} /> : item.title ? (
                                <Typography variant="h6" sx={{ fontWeight: 820, letterSpacing: '-0.03em', overflowWrap: 'anywhere' }}>
                                    {item.title}
                                </Typography>
                            ) : null}
                            {loading ? <Skeleton width="42%" height={38} /> : item.value !== null && item.value !== undefined && item.value !== '' ? (
                                <Typography sx={{ fontSize: { xs: '1.55rem', md: '1.8rem' }, lineHeight: 1.05, fontWeight: 900, color: accent, letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums' }}>
                                    {item.value}
                                </Typography>
                            ) : null}
                            {loading ? <Skeleton width="88%" /> : item.detail ? <Typography variant="body2" color="text.secondary">{item.detail}</Typography> : null}
                            {!loading && lines.length ? (
                                <Box sx={{ display: 'grid', gap: 0.55, mt: item.title || item.value ? 0.25 : 0 }}>
                                    {lines.map((line) => (
                                        <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
                                    ))}
                                </Box>
                            ) : null}
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );
};

export default function PerformanceJourneyOverview({
    eyebrow,
    title,
    description,
    metrics = [],
    supportMetrics = [],
    stageDescriptions = {},
    stageAccents = {},
    badge,
    insight,
    afterOverview,
    highlights = [],
    loading = false,
    ariaLabel = 'Primary performance indicators',
    variant = 'default',
}) {
    const theme = useTheme();
    const plain = variant === 'plain';
    const accents = { ...defaultStageAccents(theme), ...stageAccents };
    const stages = groupPerformanceStages(metrics, stageDescriptions, accents);
    const columnCount = Math.max(1, Math.min(metrics.length, 6));

    return (
        <Box
            sx={{
                display: 'grid',
                gap: { xs: 1.5, md: 2 },
                width: '100%',
                minWidth: 0,
            }}
        >
        <Card
            component="section"
            variant="outlined"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: { xs: 3, md: 4 },
                borderColor: alpha(theme.palette.primary.main, 0.14),
                background: `linear-gradient(132deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 58%, ${alpha(theme.palette.info?.main || theme.palette.primary.main, 0.08)} 100%)`,
                color: 'text.primary',
                boxShadow: `0 16px 38px ${alpha(theme.palette.primary.dark, 0.09)}`,
                ...(plain ? getReportSurfaceSx(theme) : {}),
                breakInside: 'avoid',
                minWidth: 0,
            }}
        >
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, '&:last-child': { pb: { xs: 1.5, md: 2.5 } } }}>
                <Box sx={{ mb: 1.5 }}><ReportLead eyebrow={eyebrow} title={title} description={description} badge={badge} heading={plain ? 'h2' : 'h1'} showDates={!plain} /></Box>

                <Box
                    role="group"
                    aria-label={ariaLabel}
                    sx={{
                        position: 'relative',
                        display: 'grid',
                        gap: 1.25,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            md: `repeat(${Math.min(columnCount, 3)}, minmax(0, 1fr))`,
                            lg: `repeat(${columnCount}, minmax(0, 1fr))`,
                        },
                        '&::before': {
                            content: '""',
                            display: { xs: 'none', lg: metrics.length > 1 ? 'block' : 'none' },
                            position: 'absolute',
                            top: 30,
                            left: '7%',
                            right: '7%',
                            height: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.16),
                        },
                    }}
                >
                    {metrics.map((metric, index) => (
                        <PerformanceMetric
                            key={metric.key || metric.label}
                            metric={metric}
                            accent={metric.accent || accents[metric.stage] || theme.palette.primary.main}
                            showStage={index === 0 || metrics[index - 1]?.stage !== metric.stage}
                            loading={loading}
                            plain={plain}
                        />
                    ))}
                </Box>

                {supportMetrics.length ? (
                    <Box sx={{ mt: 1.5, display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: `repeat(${Math.min(supportMetrics.length, 5)}, minmax(0, 1fr))` } }}>
                        {supportMetrics.map((metric) => (
                            <Box key={metric.key || metric.label} sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: alpha(theme.palette.background.paper, 0.58), border: `1px solid ${alpha(theme.palette.primary.main, 0.11)}` }}>
                                <Typography sx={{ ...eyebrowSx, color: 'text.secondary' }}>{metric.label}</Typography>
                                {loading ? <Skeleton width="65%" /> : (
                                    <Typography sx={{ mt: 0.25, fontSize: '0.95rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{metric.formatted ?? metric.value ?? '—'}</Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                ) : null}

                {insight ? (
                    <Box sx={{ mt: 1.25, px: 1.4, py: 1.1, borderRadius: 2, bgcolor: alpha(theme.palette.background.paper, 0.56), borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.55)}` }}>
                        <Typography sx={{ ...eyebrowSx, color: 'text.secondary' }}>What stands out</Typography>
                        {loading ? <Skeleton width="86%" /> : <Typography variant="body2" sx={{ mt: 0.3, color: 'text.primary', fontWeight: 700 }}>{insight}</Typography>}
                    </Box>
                ) : null}

                {stages.some((stage) => stage.description) ? (
                    <Box sx={{ mt: 1.5, display: { xs: 'none', lg: 'grid' }, gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, gap: 1.25 }}>
                        {stages.map((stage) => (
                            <Box key={stage.label} sx={{ gridColumn: `span ${stage.count}`, display: 'flex', gap: 0.75, alignItems: 'center', minWidth: 0 }}>
                                <Box aria-hidden="true" sx={{ width: 8, height: 8, flex: '0 0 auto', borderRadius: '50%', bgcolor: stage.accent || theme.palette.primary.main }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    <Box component="span" sx={{ fontWeight: 800, color: 'inherit' }}>{stage.label}:</Box> {stage.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ) : null}
            </CardContent>
        </Card>
        {afterOverview}
        <PerformanceHighlightsGrid items={highlights} loading={loading} plain={plain} />
        </Box>
    );
}
